import { type ChangeEvent, type FC, useEffect, useState } from 'react';

import { cn } from '@shared/utils';

import { GenericPopover } from '../GenericPopover';

export interface TimeUnitPickerProps {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  isOptionDisabled?: (opt: string) => boolean;
}

const REGEX_VALIDATION = /^\d{0,2}$/;

export const TimeUnitPicker: FC<TimeUnitPickerProps> = ({ value, onChange, options, isOptionDisabled }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    if (val === '' || REGEX_VALIDATION.test(val)) {
      setInputValue(val);

      if (options.includes(val) && !isOptionDisabled?.(val)) {
        onChange(val);
      }
    }
  };

  const handleBlur = () => {
    if (!options.includes(inputValue) || isOptionDisabled?.(inputValue)) {
      setInputValue(value);
    } else {
      onChange(inputValue);
    }
  };

  return (
    <GenericPopover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <input
          type="text"
          value={inputValue}
          onClick={() => setOpen(true)}
          onChange={handleInputChange}
          onBlur={handleBlur}
          className={cn(
            'h-8 w-12 rounded-md border border-input bg-transparent px-2 text-center text-sm placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer',
            'focus:border-primary focus:outline-none focus:ring-0 focus:ring-offset-0',
          )}
        />
      }
      contentClassName="p-1 w-[48px]"
      align="start"
      data-testid="time-unit-picker-popover"
    >
      <div
        className="max-h-40 overflow-y-auto flex flex-col gap-px scroll-container"
        data-testid="time-unit-picker-options"
        onWheel={(e) => e.stopPropagation()}
      >
        {options.map((opt) => {
          const isDisabled = isOptionDisabled?.(opt);

          return (
            <button
              key={opt}
              type="button"
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) {
                  onChange(opt);
                  setOpen(false);
                }
              }}
              className={cn(
                'w-full px-2 py-1.5 text-center text-sm rounded-sm transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                value === opt && 'bg-accent text-accent-foreground',
                isDisabled ? 'opacity-20 cursor-not-allowed' : '',
              )}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </GenericPopover>
  );
};
